/**
 * Every address of the group API, in one file.
 *
 * # Why they are constants and not strings in the screens
 *
 * The service half of this work package is being built in parallel, against the same
 * written contract. Where the two have to meet at merge, the change is ONE line here
 * rather than a search through the views.
 *
 * The base (`/api/pms/v1`) is the fetch store's; nothing below repeats it.
 */

/** The reporting tree: the root, the units under it, and every house. */
export const GROUP_TREE_PATH = '/group/tree'

/** Companies. The detail and the drift hang off the same root. */
export const LEGAL_ENTITIES_PATH = '/group/legal-entities'

/** The units of the tree — a region or a brand. */
export const ORG_UNITS_PATH = '/group/org-units'

/** `GET|PUT|DELETE /group/legal-entities/{id}`. */
export const legalEntityPath = (id: string): string => `${LEGAL_ENTITIES_PATH}/${id}`

/** `GET /group/legal-entities/{id}/drift` — where a house and its owner have parted. */
export const driftPath = (id: string): string => `${LEGAL_ENTITIES_PATH}/${id}/drift`

/** `PUT|DELETE /group/org-units/{id}`. */
export const orgUnitPath = (id: string): string => `${ORG_UNITS_PATH}/${id}`

/** `POST /group/properties/{id}/assign` — the house's owner, from a date, with its past. */
export const assignPath = (propertyId: string): string =>
    `/group/properties/${propertyId}/assign`

/** `PUT /group/properties/{id}/org-unit` — where the house reports. */
export const propertyOrgUnitPath = (propertyId: string): string =>
    `/group/properties/${propertyId}/org-unit`

/**
 * `GET /group/open/{id}?to=` — the doorway into one house's reception portal.
 *
 * The `to` is a path inside that portal and is encoded, because it carries its own
 * query more often than not.
 */
export const openPath = (propertyId: string, to = '/'): string =>
    `/group/open/${propertyId}?to=${encodeURIComponent(to)}`

/* ────────────────────────────────────────────────────────────────────────────
 * G1 — the consolidated screens.
 *
 * Same rule as above: the service half is being built in parallel against the same
 * written contract, so every address is one line here rather than a string in a screen.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET|PUT /group/settings` — the report currency and the group's time zone. */
export const GROUP_SETTINGS_PATH = '/group/settings'

/** `GET /group/fx-rates?from=&to=` — the monthly rates, and the months nobody has filled. */
export const FX_RATES_PATH = '/group/fx-rates'

/** `PUT /group/fx-rates/{currency}/{month}` — one month of one currency. */
export const fxRatePath = (currency: string, month: string): string =>
    `${FX_RATES_PATH}/${encodeURIComponent(currency)}/${encodeURIComponent(month)}`

/** `GET /group/fx-rates/suggest?currency=&month=` — a PROPOSAL, never a saved rate. */
export const fxSuggestPath = (currency: string, month: string): string =>
    `${FX_RATES_PATH}/suggest?currency=${encodeURIComponent(currency)}&month=${encodeURIComponent(month)}`

/** `GET /group/kpi/daily?date=&properties=` — the flash, one row per house. */
export const KPI_DAILY_PATH = '/group/kpi/daily'

/** `GET /group/kpi/pickup?months=&properties=` — what is on the books, month by month. */
export const KPI_PICKUP_PATH = '/group/kpi/pickup'

/**
 * `GET /group/materials/kpi?month=&properties=` — the procurement board: one month of what
 * the group bought, by house and by supplier (`group:finance`).
 */
export const MATERIALS_KPI_PATH = '/group/materials/kpi'

/* ────────────────────────────────────────────────────────────────────────────
 * G2 — the plan, and what happened to it (docs/UPRAVA.md §31).
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `GET /group/budgets?year=&properties=` — the plan grid (`group:report`),
 * `PUT /group/budgets` — the cells somebody edited (`group:finance` or `group:manage`).
 *
 * One address for both, because it is one register: what is read is what is written, and a
 * second path for the write would be a second place the year could be spelled.
 */
export const GROUP_BUDGETS_PATH = '/group/budgets'

/** `POST /group/budgets/copy` — a whole year carried forward, optionally scaled. */
export const GROUP_BUDGETS_COPY_PATH = `${GROUP_BUDGETS_PATH}/copy`

/** `GET /group/budget-vs-actual?year=&month=&properties=` — the plan beside the takings. */
export const GROUP_BUDGET_VS_ACTUAL_PATH = '/group/budget-vs-actual'

/* ────────────────────────────────────────────────────────────────────────────
 * G15 — izkaz skupine (light) (docs/UPRAVA.md §44).
 *
 * `group:report` OR `group:finance` on both, which is the pair the monthly close's reads
 * carry: this is a controller's board and a finance director's board at once.
 *
 * Two addresses and not one with a flag, because they answer two questions and are read by
 * two different things on the screen — a table that is redrawn whenever the fold changes,
 * and a chart that is not.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET /group/pnl?month=&by=house|company|group&properties=` — one month of the statement. */
export const GROUP_PNL_PATH = '/group/pnl'

/** `GET /group/pnl/trend?months=&properties=` — the group's own line, month by month. */
export const GROUP_PNL_TREND_PATH = `${GROUP_PNL_PATH}/trend`

/** `GET /group/live/today?properties=` — the houses as they stand right now. */
export const LIVE_TODAY_PATH = '/group/live/today'

/** `GET /group/overview?properties=` — „kaj me čaka“, in three panels. */
export const GROUP_OVERVIEW_PATH = '/group/overview'

/** `POST /group/snapshots/rebuild` — re-read a range of days out of the houses. */
export const SNAPSHOTS_REBUILD_PATH = '/group/snapshots/rebuild'

/** `GET /group/operations?limit=` — what the group has been asked to do, and how it went. */
export const OPERATIONS_PATH = '/group/operations'

/**
 * `POST /group/operations/{operationId}/retry` — the press again, for the houses that
 * refused.
 *
 * The one WRITE of the register, and it is a write of the ACT and not of the log: nothing
 * POSTs a row into this collection. The row a retry leaves is written by the original
 * press's own claim, which is why the answer is the NEW operation and not the old one.
 *
 * The segment is the press's OWN name — the register row's `operation_id`, not its `id`.
 * The two are different strings and the door reads the first
 * (`Logic.RetryGroupOperation` → `GetGroupOperationByOperationId`), so a path built from
 * the document id is a retry of a press that does not exist. Typed with the name it takes
 * so that the confusion cannot be made silently.
 *
 * `group:manage`, deliberately not the right of the act being retried: a retry can reach a
 * rate push, an export and a send, and a door whose right depended on which row somebody
 * clicked would be four doors with one path.
 */
export const operationRetryPath = (operationId: string): string =>
    `${OPERATIONS_PATH}/${operationId}/retry`

/**
 * `GET /group/channels/health?properties=` — whether the group is still SELLING (WP-G9).
 *
 * Not a figure about a day, which is why it is not on the flash: a channel that stopped
 * pushing on Tuesday has a perfectly ordinary Tuesday in the snapshots, because the rooms
 * that were already sold are still sold. What stopped is the selling.
 */
export const CHANNEL_HEALTH_PATH = '/group/channels/health'

/* ────────────────────────────────────────────────────────────────────────────
 * G4 — Milo for the head office, and the group's morning report.
 *
 * Same rule again: the service half is built in parallel against the same written
 * contract, so every address is one line here.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `POST /group/chat` — one question. Answers 202 with the turn to come back for. */
export const GROUP_CHAT_PATH = '/group/chat'

/** `GET /group/chat/turns/{id}` — is the answer written yet, and what is it. */
export const groupChatTurnPath = (turnId: string): string =>
    `${GROUP_CHAT_PATH}/turns/${turnId}`

/** `GET /group/chat/conversations` — this account's own threads, newest first. */
export const GROUP_CONVERSATIONS_PATH = `${GROUP_CHAT_PATH}/conversations`

/** `GET|DELETE /group/chat/conversations/{id}`. */
export const groupConversationPath = (id: string): string =>
    `${GROUP_CONVERSATIONS_PATH}/${id}`

/** `GET|POST /group/reports` — the reports this account has written. */
export const GROUP_REPORTS_PATH = '/group/reports'

/** `GET|PUT|DELETE /group/reports/{id}`. */
export const groupReportPath = (id: string): string => `${GROUP_REPORTS_PATH}/${id}`

/** `POST /group/reports/{id}/run` — a tek now, rather than at the hour it is due. */
export const groupReportRunNowPath = (id: string): string =>
    `${GROUP_REPORTS_PATH}/${id}/run`

/** `GET /group/reports/{id}/runs?limit=` — the teki of one report, newest first. */
export const groupReportRunsPath = (id: string, limit = 20): string =>
    `${GROUP_REPORTS_PATH}/${id}/runs?limit=${limit}`

/** `GET /group/reports/{id}/runs/{runId}` — one tek, with every razdelek in it. */
export const groupReportRunPath = (id: string, runId: string): string =>
    `${GROUP_REPORTS_PATH}/${id}/runs/${runId}`

/* ────────────────────────────────────────────────────────────────────────────
 * G16 — the register of people (docs/UPRAVA.md §29).
 *
 * `group:hr` and not `group:read`: §8 made seven rights so that the person who draws
 * the organisation chart does not thereby read who works under which contract in every
 * hotel. The screen declares the right, the service enforces it.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET /group/staff/people?on=&properties=&q=&entity=` — one row per person. */
export const GROUP_PEOPLE_PATH = '/group/staff/people'

/** `GET /group/staff/people/{userId}` — one person's periods of work over time. */
export const groupPersonPath = (userId: string): string => `${GROUP_PEOPLE_PATH}/${userId}`

/**
 * `PUT /group/staff/employments/{id}/legal-entity` — the head office's correction.
 *
 * The PERIOD and not the person: somebody with three seasons behind them has three
 * contracts, possibly under three companies, and a path naming the person would have to
 * guess which one a press was about.
 */
export const groupStaffLegalEntityPath = (employmentId: string): string =>
    `/group/staff/employments/${employmentId}/legal-entity`

/**
 * The reception portal's own personnel screen for one person — what „Odpri kadrovsko“
 * hands to `GET /group/open/{propertyId}?to=`.
 *
 * A path INSIDE that portal and never an absolute address: the doorway mints the way in
 * and checks the path on its own side, and a console writing a whole URL would be writing
 * an open redirect for the door beside it.
 */
export const receptionPersonPath = (userId: string): string => `/staff/people/${userId}`

/* ────────────────────────────────────────────────────────────────────────────
 * G18 — the company's payroll file (docs/UPRAVA.md §33).
 *
 * `group:hr`, the same key the register of people opened: §8 grants „ljudje, pogodbe po
 * družbi, plače“ in one sentence, and this is its third clause.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `GET /group/companies/{id}/payroll/{YYYY-MM}` — one company's month, one row per
 * person per house.
 *
 * The month is a path SEGMENT and not a query parameter, exactly as the house's own
 * closed-month routes spell it: it is the thing being asked about rather than a filter
 * over it.
 */
export const groupPayrollPath = (entityId: string, month: string): string =>
    `/group/companies/${entityId}/payroll/${month}`

/**
 * `GET /group/companies/{id}/payroll/{YYYY-MM}.csv` — the same month as the file a
 * payroll bureau loads.
 *
 * The extension and not an `Accept` header, because this is a URL somebody pastes into a
 * scheduler and opens in a browser first.
 */
export const groupPayrollExportPath = (entityId: string, month: string): string =>
    `${groupPayrollPath(entityId, month)}.csv`

/* ────────────────────────────────────────────────────────────────────────────
 * G13 — the group's own monthly close (docs/UPRAVA.md §32).
 *
 * Same rule as everything above: the service half is built in parallel against the same
 * written contract, so every address is one line here rather than a string in a screen.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `GET /group/close/{YYYY-MM}?properties=` — the sheet, with the stored close on it.
 *
 * The narrowing is honoured on the READ and refused on the two writes, which is the one
 * asymmetry of this feature and is the whole of what a group close means: a regional
 * director may look at how her six hotels stand, and nobody shuts a month for six of forty.
 */
export const groupClosePath = (month: string): string =>
    `/group/close/${encodeURIComponent(month)}`

/** `POST /group/close/{month}/close` — the decision. Sends `operation_id` and never houses. */
export const groupCloseSubmitPath = (month: string): string =>
    `${groupClosePath(month)}/close`

/** `POST /group/close/{month}/reopen` — the way back, with a reason. */
export const groupCloseReopenPath = (month: string): string =>
    `${groupClosePath(month)}/reopen`

/** `GET /group/closes?limit=` — the months the group has declared finished, newest first. */
export const groupClosesPath = (limit = 12): string => `/group/closes?limit=${limit}`

/**
 * Where the console's OWN repair lives for the one line no hotel can fix: the monthly rate.
 *
 * A route name would be tidier and is deliberately not used — this file is addresses, and
 * the screen resolves it through the router it already has.
 */
export const GROUP_CLOSE_FX_ROUTE = 'fx'

/* ────────────────────────────────────────────────────────────────────────────
 * G14 — knjigovodstvo po družbi (docs/UPRAVA.md §34).
 *
 * `group:finance`, like „Nabava“ and for a stronger reason than that board has: this is
 * not a figure about buying, it is the ACT of handing a company's purchases to its
 * book-keeper.
 *
 * Everything hangs off ONE company and never off a house, which is the whole shape of the
 * package: the credentials that reach the book-keeping system stay sealed per house and
 * the console never sees them, so what a company owns here is the ORCHESTRATION and the
 * map of accounts — not a key.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `/group/companies/{id}` — the root every address below hangs off. */
export const groupCompanyPath = (entityId: string): string => `/group/companies/${entityId}`

/**
 * `GET|POST /group/companies/{id}/ap-export` — what a period comes to, and the press.
 *
 * One address and two methods, because they are the same act asked about and then done.
 * The GET writes nothing at all; the POST is what reaches another company's system.
 */
export const companyAPExportPath = (entityId: string): string =>
    `${groupCompanyPath(entityId)}/ap-export`

/** `GET /group/companies/{id}/ap-exports` — the houses' hand-off history, merged. */
export const companyAPExportsPath = (entityId: string): string =>
    `${groupCompanyPath(entityId)}/ap-exports`

/** `GET|PUT /group/companies/{id}/category-accounts` — the company's own map. */
export const companyCategoryAccountsPath = (entityId: string): string =>
    `${groupCompanyPath(entityId)}/category-accounts`

/**
 * `GET /group/companies/{id}/category-accounts/coverage` — where the map reaches.
 *
 * Per HOUSE, because „is this category mapped“ is not a question about a map: the
 * categories are the houses' own free text, and a company may hold twelve rows for
 * categories no hotel uses while the one that carries half the cellar is unmapped.
 */
export const companyCategoryCoveragePath = (entityId: string): string =>
    `${groupCompanyPath(entityId)}/category-accounts/coverage`

/**
 * Where „Odpri knjigovodstvo“ lands in the reception portal: that house's own accounting
 * screen, which is where its credentials are entered and where a mismatched organisation
 * is corrected.
 *
 * A path INSIDE that portal and never an absolute address — the doorway mints the way in
 * and checks the path on its own side.
 */
export const receptionAccountingPath = (): string => '/materials/accounting'

/* ────────────────────────────────────────────────────────────────────────────
 * G6 — korporativne pogodbe skupine (docs/UPRAVA.md §36).
 *
 * `group:sales` for the writes and an OR of it with `group:read` for the reads, which is
 * the service's own door: who negotiates a group's corporate rates is a thing every group
 * answers differently, and a regional director who cannot see which agreements bind her
 * hotels cannot answer for their prices.
 *
 * Same rule as everything above: the service half is built in parallel against the same
 * written contract, so every address is one line here rather than a string in a screen.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `GET|POST /group/corporate-contracts?company_id=&status=&properties=` — the register,
 * and a new draft.
 *
 * One address for both, because it is one register: what is read is what is written, and a
 * second path for the write would be a second place a filter could be spelled.
 */
export const CORPORATE_CONTRACTS_PATH = '/group/corporate-contracts'

/** `GET|PUT|DELETE /group/corporate-contracts/{id}`. DELETE takes a draft and nothing else. */
export const corporateContractPath = (id: string): string =>
    `${CORPORATE_CONTRACTS_PATH}/${id}`

/**
 * `POST /group/corporate-contracts/{id}/activate` — the press that puts one in force.
 *
 * Its own door and never a status field on the PUT: this is the one act in the register
 * with a refusal behind it — every house must carry a rate plan with the contract's code —
 * and a save that could do it would bind nine hotels to a price by accident.
 */
export const corporateActivatePath = (id: string): string =>
    `${corporateContractPath(id)}/activate`

/** `POST /group/corporate-contracts/{id}/end` — the way out, with a reason. */
export const corporateEndPath = (id: string): string =>
    `${corporateContractPath(id)}/end`

/** `GET /group/corporate-production?from=&to=&company_id=&properties=` — what they brought. */
export const CORPORATE_PRODUCTION_PATH = '/group/corporate-production'

/**
 * `GET /group/rate-plan-codes?properties=` — the codes the houses in scope actually carry.
 *
 * The helper beside the dialog's code field. It belongs to this package and not to the rate
 * plans' own branch: `/properties/{id}/rate-plans` is ONE house's register, and what a
 * contract needs is the one name that means the same thing in nine of them.
 */
export const CORPORATE_RATE_PLAN_CODES_PATH = '/group/rate-plan-codes'

/**
 * `GET /companies?search=&active=true&limit=` — the firm a contract is with.
 *
 * The tenant's own register of payers and not a group route, because there is only one of
 * it: a company is tenant-wide (`DataStructures/Company.go`) and an agreement over nine
 * hotels is still with one firm. It is guarded by `guest:read`, so an account holding only
 * the group rights is refused — and the dialog prints the service's own sentence rather
 * than an empty list, which would read as „this firm does not exist“.
 */
export const companyLookupPath = (search: string, limit = 20): string => {
    const query = new URLSearchParams({ active: 'true', limit: String(limit) })

    if (search.trim()) query.set('search', search.trim())

    return `/companies?${query.toString()}`
}

/* ────────────────────────────────────────────────────────────────────────────
 * G12 — medpodjetniški račun iz prenosa zaloge (docs/UPRAVA.md §38).
 *
 * `group:finance`, the third screen of this console behind it: what one company of the
 * group charges another for stock that crossed between their houses is a document two
 * taxpayers file, not a board anybody may read.
 *
 * Same rule as everything above — the service half is built in parallel against the same
 * written contract, so every address is one line here rather than a string in a screen.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET /group/intercompany?month=&properties=` — the month's book, one row per pair. */
export const INTERCOMPANY_PATH = '/group/intercompany'

/** `GET /group/intercompany/invoices?month=&status=&seller=&buyer=&properties=`. */
export const INTERCOMPANY_INVOICES_PATH = `${INTERCOMPANY_PATH}/invoices`

/** `POST /group/intercompany/invoices/merge` — several drafts of ONE pair into one. */
export const INTERCOMPANY_MERGE_PATH = `${INTERCOMPANY_INVOICES_PATH}/merge`

/** `GET /group/intercompany/invoices/{id}` — the row, plus the lines and both companies. */
export const intercompanyInvoicePath = (id: string): string =>
    `${INTERCOMPANY_INVOICES_PATH}/${id}`

/** `POST /group/intercompany/invoices/{id}/issue` — the press that reserves a number. */
export const intercompanyInvoiceIssuePath = (id: string): string =>
    `${intercompanyInvoicePath(id)}/issue`

/** `POST /group/intercompany/invoices/{id}/cancel` — the way back, with a reason. */
export const intercompanyInvoiceCancelPath = (id: string): string =>
    `${intercompanyInvoicePath(id)}/cancel`

/**
 * `GET /group/intercompany/invoices/{id}/pdf` — the document itself.
 *
 * Fetched with the session like every other read and handed to the browser as a blob
 * (`views/payroll/Payroll.vue` is the precedent). Never an `<a href>`: the route carries
 * the bearer token, and a plain link would arrive unauthenticated and save the sign-in
 * screen's refusal as a .pdf.
 */
export const intercompanyInvoicePdfPath = (id: string): string =>
    `${intercompanyInvoicePath(id)}/pdf`

/** `GET|PUT /group/intercompany/pairs` — the standing rules. The PUT upserts on the pair. */
export const INTERCOMPANY_PAIRS_PATH = `${INTERCOMPANY_PATH}/pairs`

/** `DELETE /group/intercompany/pairs/{id}`. */
export const intercompanyPairPath = (id: string): string =>
    `${INTERCOMPANY_PAIRS_PATH}/${id}`

/* ────────────────────────────────────────────────────────────────────────────
 * G5 — centralne predloge cenikov (docs/UPRAVA.md §35).
 *
 * `group:sales` and not `group:read`: what forty hotels charge is central sales'
 * decision (§8's circles of trust), and a board anybody may read is not the same thing
 * as a press that rewrites every house's prices.
 *
 * Same rule as everything above: the service half is built in parallel against the same
 * written contract, so every address is one line here rather than a string in a screen.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET|POST /group/rate-templates` — the templates the group sells from. */
export const RATE_TEMPLATES_PATH = '/group/rate-templates'

/** `GET|PUT|DELETE /group/rate-templates/{id}`. */
export const rateTemplatePath = (id: string): string => `${RATE_TEMPLATES_PATH}/${id}`

/**
 * `GET /group/rate-templates/{id}/preview?properties=` — what a push WOULD do, house by
 * house, before anybody presses anything.
 *
 * The narrowing is not spelled here and never is: `libs/scopeParams.ts`'s `scopedPath`
 * puts it on, which is the one place that knows the whole group sends nothing at all.
 */
export const rateTemplatePreviewPath = (id: string): string => `${rateTemplatePath(id)}/preview`

/** `POST /group/rate-templates/{id}/push` — the press. Answers the shared operation. */
export const rateTemplatePushPath = (id: string): string => `${rateTemplatePath(id)}/push`

/**
 * `GET /group/rates/parity?properties=&template_id=` — where a house has drifted from the
 * template its plan was pushed from.
 *
 * Deliberately NOT named beside `FX_RATES_PATH`, although English calls both of them
 * rates: those are the monthly EXCHANGE rates a group consolidates in and these are
 * prices a guest pays. Slovene keeps the two words apart („tečaj“, „cena“) and so do the
 * constants, because a screen reaching for the wrong one would compile.
 */
export const RATE_PARITY_PATH = '/group/rates/parity'

/**
 * `POST /group/restrictions/bulk` — one window of restrictions written over many houses.
 *
 * Its own address and not a mode of the push: a push carries a whole template — prices,
 * the cancellation ladder, the categories — and this carries the six fields a revenue
 * manager changes on a Thursday afternoon for one weekend.
 */
export const GROUP_RESTRICTIONS_BULK_PATH = '/group/restrictions/bulk'

/** `GET|POST /group/room-categories` — the group's own vocabulary of room categories. */
export const ROOM_CATEGORIES_PATH = '/group/room-categories'

/** `PUT|DELETE /group/room-categories/{id}`. */
export const roomCategoryPath = (id: string): string => `${ROOM_CATEGORIES_PATH}/${id}`

/**
 * `GET /group/room-categories/coverage?properties=` — which room type of which house
 * carries a category, and which carries none.
 *
 * Per HOUSE and never a count, for the same reason the accounting map's coverage is:
 * the vocabulary is the group's and the room types are the houses' own, so a group may
 * hold twelve categories nobody has mapped a room to while the one carrying half the
 * beds is missing from the very hotel a push is meant for.
 */
export const ROOM_CATEGORY_COVERAGE_PATH = `${ROOM_CATEGORIES_PATH}/coverage`

/**
 * Where „Odpri cenik“ lands in the reception portal: that house's own rates screen, which
 * is where a plan pushed from here is read, overridden and sold from.
 *
 * A path INSIDE that portal and never an absolute address — the doorway mints the way in
 * and checks the path on its own side, and a console writing a whole URL would be writing
 * an open redirect for the door beside it.
 */
export const receptionRatePlansPath = (): string => '/rate-plans'

/* ────────────────────────────────────────────────────────────────────────────
 * G7 — the central reservation service and the guest across the group
 * (docs/UPRAVA.md §37).
 *
 * `group:sales` — and it is the FIRST right in this console that opens a WRITE. §8 made
 * seven rights so that the person who draws the organisation chart does not thereby sell
 * rooms; decision 6 of 16. 9. 2026 gives this one a booking into any house in scope and a
 * move between houses. The reads are `group:sales` OR `group:read`, which is the same door
 * the overview and the channel board take: seeing what the group has free is not selling it.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `GET /group/availability?arrival=&departure=&adults=&category=&properties=` — the grid:
 * houses down, room types across, with the quote beside the free rooms.
 *
 * It answers per PERIOD and never per date — how many rooms could take the WHOLE stay,
 * which is the only figure a sales office can sell against.
 */
export const GROUP_AVAILABILITY_PATH = '/group/availability'

/** `GET|POST /group/reservations` — the sales desk's own root. */
export const GROUP_RESERVATIONS_PATH = '/group/reservations'

/**
 * `GET /group/reservations/lookup?property_id=&number=` — one booking by the number read
 * off a confirmation, as the little card the transfer screen checks itself against.
 *
 * An EXACT number and not a search: the screen is about to move somebody's room, and a list
 * of near matches is a list somebody picks the wrong row out of.
 */
export const groupReservationLookupPath = (propertyId: string, number: string): string =>
    `${GROUP_RESERVATIONS_PATH}/lookup?property_id=${encodeURIComponent(propertyId)}` +
    `&number=${encodeURIComponent(number)}`

/**
 * `POST /group/reservations/{propertyId}/{reservationId}/move` — the transfer.
 *
 * The SENDING house is in the path and not in the body, because that is the house the
 * service checks the caller's right in: a move is an act done to a booking that lives
 * there.
 */
export const groupReservationMovePath = (propertyId: string, reservationId: string): string =>
    `${GROUP_RESERVATIONS_PATH}/${propertyId}/${reservationId}/move`

/** `GET /group/guests?q=&limit=` — the guest book, by name. Tenant-wide, like `/guests`. */
export const GROUP_GUESTS_PATH = '/group/guests'

/** `GET /group/guests/{guestId}/profile?properties=` — one person across the whole group. */
export const groupGuestProfilePath = (guestId: string): string =>
    `${GROUP_GUESTS_PATH}/${guestId}/profile`

/**
 * `GET /companies?search=` — the firms that pay for rooms.
 *
 * The reception portal's OWN route and not a group one, because a company is tenant-wide
 * exactly as a guest is (`HTTP_API/Permission.go`), and there is nothing for a group
 * endpoint to add to it. It is here rather than as a string in the dialog for this file's
 * own reason: one line to change at merge.
 */
export const COMPANIES_PATH = '/companies'

/**
 * Where „Odpri v recepciji“ lands for one booking — that house's own reservation screen.
 *
 * A path INSIDE that portal and never an absolute address: the doorway mints the way in and
 * checks the path on its own side, and a console writing a whole URL would be writing an
 * open redirect for the door beside it.
 */
export const receptionReservationPath = (reservationId: string): string =>
    `/reservations/${reservationId}`

/* ────────────────────────────────────────────────────────────────────────────
 * G17 — posoje osebja med hišami (docs/UPRAVA.md §39).
 *
 * `group:hr`, the same key „Ljudje“ and „Plače“ open: §8 grants „ljudje, pogodbe po
 * družbi, plače“ in one sentence, and lending a receptionist to the second hotel is an
 * act inside that circle rather than beside it.
 *
 * The month is a QUERY parameter here and not a path segment, unlike the payroll file's.
 * The difference is what the address IS: a payroll file is a document somebody pastes
 * into a scheduler and downloads, and this is a screen whose month is a control — the
 * same shape „Nabava“ and „Proračun“ use.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET|POST /group/staff/loans?month=&properties=&status=` — the register and the write. */
export const GROUP_LOANS_PATH = '/group/staff/loans'

/** `GET /group/staff/loans/summary?month=&properties=` — one row per pair of houses. */
export const GROUP_LOANS_SUMMARY_PATH = `${GROUP_LOANS_PATH}/summary`

/** `GET|PUT /group/staff/loans/{id}` — one arrangement, and the correction of it. */
export const groupLoanPath = (loanId: string): string => `${GROUP_LOANS_PATH}/${loanId}`

/**
 * `POST /group/staff/loans/{id}/cancel` — the withdrawal.
 *
 * A POST and not a DELETE, because withdrawing a loan is an EVENT with an author and a
 * reason and it leaves the row readable: the receiving house may have built a week around
 * it, and a deleted document explains nothing to the supervisor who finds the hole.
 */
export const groupLoanCancelPath = (loanId: string): string => `${groupLoanPath(loanId)}/cancel`

/**
 * `GET /group/staff/departments?property_id=` — the RECEIVING house's departments.
 *
 * The receiving house's own codebook and never the lender's: a person lent to the second
 * hotel stands on that hotel's rota, under that hotel's department (docs/OSEBJE.md §5).
 */
export const groupStaffDepartmentsPath = (propertyId: string): string =>
    `/group/staff/departments?property_id=${propertyId}`

/** Where „Odpri urnik“ lands in the receiving house's reception portal. */
export const receptionRotaPath = (): string => '/staff/rota'

/* ────────────────────────────────────────────────────────────────────────────
 * WP-G21 — the marketing board (docs/UPRAVA.md §43).
 *
 * Three reads and no writes: the board measures, it decides nothing. All three take the
 * same window and the same narrowing, which is why the screen builds them through one
 * `scopedPath` call each rather than assembling a query string per tile.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `GET /group/marketing/roi?from=&to=&group_by=&q=&properties=` — what the group's
 * advertising brought back, merged across the houses.
 */
export const MARKETING_ROI_PATH = '/group/marketing/roi'

/**
 * `GET /group/marketing/roi/guests?campaign=&from=&to=&limit=&properties=` — the people one
 * merged row brought, with their value across the whole group.
 *
 * `campaign` is the ROW's own key and not a campaign handle. A handle is house-local — two
 * hotels may mint the same word — so a drawer keyed on one would answer about whichever
 * house happened to use it first.
 */
export const MARKETING_ROI_GUESTS_PATH = `${MARKETING_ROI_PATH}/guests`

/** `GET /group/marketing/roi/trend?months=&properties=` — the spark line, month by month. */
export const MARKETING_ROI_TREND_PATH = `${MARKETING_ROI_PATH}/trend`

/**
 * Where „Odpri v recepciji“ lands from a guest row.
 *
 * The house's own guest REGISTER and not a card, because the reception portal has no
 * per-guest address: `/guests` is a list with a search box and nothing hangs off it
 * (`pms-web/src/views/guest/Guests.vue`). The drawer therefore prints the person's name
 * beside the button, so that whoever follows it knows what to type. When the reception
 * portal grows a guest page, this one line moves.
 */
export const RECEPTION_GUESTS_PATH = '/guests'

/* ────────────────────────────────────────────────────────────────────────────
 * G8 — matične skupine čez več hiš (docs/UPRAVA.md §40).
 *
 * `group:sales`, the same key „Rezervacije“, „Pogodbe“ and „Ceniki“ open: §8 puts central
 * sales and reservations in one circle of trust, and a congress booked into four hotels is
 * inside it rather than beside it.
 *
 * The congress is what every path names. The pickup and the release carry their HOUSE in the
 * body beside the group and the room type they are about — three ids that belong together,
 * and a path segment repeating one of them would be two places one hotel is named.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET|POST /group/master-groups?status=&from=&to=&properties=` — the register and the create. */
export const MASTER_GROUPS_PATH = '/group/master-groups'

/** `GET|PUT /group/master-groups/{id}` — one congress, and the edit of what it owns. */
export const masterGroupPath = (id: string): string => `${MASTER_GROUPS_PATH}/${id}`

/** `POST /group/master-groups/{id}/status` — the congress and every house under it. */
export const masterGroupStatusPath = (id: string): string => `${masterGroupPath(id)}/status`

/**
 * `POST /group/master-groups/{id}/resync` — the contract written into its houses again.
 *
 * A door of its own and not „press Save again“, for the service's own reason: Save is an
 * EDIT and this is a REPAIR. An edit rewrites the congress's fields and propagates two of
 * them, so reaching one house that was unreachable at the time would mean saving a document
 * nobody wants changed — which writes a new editor over the person who actually decided
 * something, in the trail a controller reads.
 */
export const masterGroupResyncPath = (id: string): string => `${masterGroupPath(id)}/resync`

/** `POST /group/master-groups/{id}/members` — one more hotel, added later. */
export const masterGroupMembersPath = (id: string): string => `${masterGroupPath(id)}/members`

/**
 * `GET|POST /group/master-groups/{id}/pickup` — the joint board, and the press.
 *
 * One address for both, because they are the same thing seen and done: the grid says how
 * much of the congress is still unclaimed, and the button on a cell of it claims some.
 */
export const masterGroupPickupPath = (id: string): string => `${masterGroupPath(id)}/pickup`

/** `POST /group/master-groups/{id}/release` — n held rooms of one house, given back. */
export const masterGroupReleasePath = (id: string): string => `${masterGroupPath(id)}/release`

/** `GET /group/master-groups/{id}/rooming` — one door per house, and what is behind each. */
export const masterGroupRoomingPath = (id: string): string => `${masterGroupPath(id)}/rooming`

/**
 * Where „Odpri v recepciji“ lands for one house of a congress — that hotel's own group screen.
 *
 * A path INSIDE that portal and never an absolute address, for `receptionReservationPath`'s
 * own reason: the doorway mints the way in and checks the path on its own side, and a console
 * writing a whole URL would be writing an open redirect for the door beside it.
 */
export const receptionGroupPath = (groupId: string): string => `/skupine/${groupId}`

/* ────────────────────────────────────────────────────────────────────────────
 * G19 — kadrovski kazalniki skupine (docs/UPRAVA.md §42).
 *
 * `group:hr` OR `group:report`, which is the ONE door of §8's personnel clause that is
 * not `group:hr` alone — because it is the one that answers no person. There are no
 * names on this board: counts, minutes, money, and a department.
 *
 * The month is a QUERY parameter and not a path segment, like „Nabava“ and „Posoje“ and
 * unlike the payroll file's: this is a screen whose month is a CONTROL, not a document
 * somebody pastes into a scheduler and downloads.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET /group/staff/kpi?month=&properties=` — one month, one row per house. */
export const GROUP_STAFF_KPI_PATH = '/group/staff/kpi'

/** `GET /group/staff/kpi/trend?months=&properties=` — the same totals, month by month. */
export const GROUP_STAFF_KPI_TREND_PATH = `${GROUP_STAFF_KPI_PATH}/trend`

/**
 * Where „Odpri kadrovsko“ lands in the reception portal: that house's own working-time
 * record, which is the screen every hour on this board came off — and the one place a
 * figure somebody disagrees with can be taken apart, day by day and person by person.
 */
export const RECEPTION_STAFF_TIME_PATH = '/staff/time'

/* ────────────────────────────────────────────────────────────────────────────
 * G20 — skupinske kampanje in predloge znamke (docs/UPRAVA.md §41).
 *
 * `group:marketing`, and it is the first use of that key anywhere: §8 named it with WP-G0
 * and nothing has guarded on it until now. The READS are narrower than „Ceniki“'s — that
 * key alone and not „or `group:read`“ — because these screens carry an AUDIENCE, and §8
 * made marketing its own circle of trust precisely so that „sees the group“ does not imply
 * „sees the mailing list“.
 *
 * The scope is not spelled here and never is: `libs/scopeParams.ts`'s `scopedPath` puts it
 * on, which is the one place that knows the whole group sends nothing at all.
 * ──────────────────────────────────────────────────────────────────────────── */

/** `GET|POST /group/marketing/suppressions?q=&limit=` — the one list the whole group is bound by. */
export const GROUP_SUPPRESSIONS_PATH = '/group/marketing/suppressions'

/**
 * `DELETE /group/marketing/suppressions/{id}` — and it answers 409 on everything but a
 * `manual` row, because an unsubscribe is undone by nobody but the guest.
 *
 * The console hides the button on every row the service marks `removable: false`, so a
 * refusal that gets through at all is a stale screen.
 */
export const groupSuppressionPath = (suppressionId: string): string =>
    `${GROUP_SUPPRESSIONS_PATH}/${suppressionId}`

/** `GET|POST /group/marketing/templates?unit=` — a unit's own plus what it inherits. */
export const GROUP_TEMPLATES_PATH = '/group/marketing/templates'

/** `GET|PUT|DELETE /group/marketing/templates/{id}`. */
export const groupTemplatePath = (templateId: string): string =>
    `${GROUP_TEMPLATES_PATH}/${templateId}`

/** `GET|POST /group/marketing/campaigns?status=` — the register, and a new draft. */
export const GROUP_CAMPAIGNS_PATH = '/group/marketing/campaigns'

/** `GET|PUT|DELETE /group/marketing/campaigns/{id}`. DELETE takes a draft and nothing else. */
export const groupCampaignPath = (campaignId: string): string =>
    `${GROUP_CAMPAIGNS_PATH}/${campaignId}`

/**
 * `GET /group/marketing/campaigns/{id}/audience?properties=` — who this would write to.
 *
 * A pure READ that resolves forty audiences. It is the counts a person confirms against
 * before pressing Send, and the SEND resolves them again through the same function — so a
 * guest who withdrew consent this morning is not written to this afternoon because somebody
 * opened the drawer before lunch.
 */
export const groupCampaignAudiencePath = (campaignId: string): string =>
    `${groupCampaignPath(campaignId)}/audience`

/**
 * `POST /group/marketing/campaigns/{id}/send` — the press.
 *
 * ONE address for both buttons: a body carrying `test_to` posts one specimen out of the
 * group's own sending house and touches nothing, and a body without it writes to every
 * house of the scope. The console holds ONE operation id for the real send across every
 * retry (`libs/operationId.ts`) — a name minted inside the call would make a retry a second
 * mailing of forty thousand letters.
 */
export const groupCampaignSendPath = (campaignId: string): string =>
    `${groupCampaignPath(campaignId)}/send`

/** `POST /group/marketing/campaigns/{id}/cancel` — and it does not undo a send. */
export const groupCampaignCancelPath = (campaignId: string): string =>
    `${groupCampaignPath(campaignId)}/cancel`

/** `GET /group/marketing/campaigns/{id}/funnel?from=&to=` — the ROI read. */
export const groupCampaignFunnelPath = (campaignId: string): string =>
    `${groupCampaignPath(campaignId)}/funnel`
